import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';

const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { resetPassword, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  // Clear any existing errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Call the reset password API with static payload and dynamic email
      await resetPassword(data.email);
      setIsEmailSent(true);
      toast.success('Password reset email sent successfully!');
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'Failed to send password reset email';
      
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.non_field_errors && Array.isArray(error.non_field_errors)) {
          errorMessage = error.non_field_errors[0];
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
          <div className="text-center">
           
            
            <div className="mt-8 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
                <svg className="h-8 w-8 text-green-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-extrabold  dark:text-white animate-slide-up">
                Check Your Email
              </h2>
              <p className="mt-4 text-base text-white dark:text-white leading-relaxed animate-slide-up animation-delay-100">
                We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up animation-delay-200">
          <div className="card card-body shadow-xl border-0 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-white dark:text-white">
                  Didn't receive the email? Check your spam folder or
                </p>
                <button
                  type="button"
                  onClick={() => setIsEmailSent(false)}
                  className="mt-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors duration-200"
                >
                  try again with a different email
                </button>
              </div>

              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-6">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Login
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Create new account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        <div className="text-center">
          <h2 className="mt-8 text-center text-2xl md:text-3xl font-extrabold  dark:text-white animate-slide-up">
            Reset Your Password
          </h2>
          <p className="mt-4 text-center text-base text-white dark:text-white leading-relaxed animate-slide-up animation-delay-100">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up animation-delay-200">
        <div className="card card-body shadow-xl border-0 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div>
              <label className="block text-sm font-semibold text-white dark:text-white mb-4 text-center">
                Email Address
              </label>
              <div className="relative">
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className="w-full h-12 px-4 text-center text-lg border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all duration-200 hover:border-primary-400 dark:hover:border-primary-400 focus:scale-[1.02] focus:shadow-lg"
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <p className="mt-4 text-xs text-white dark:text-white text-center">
                We'll send a password reset link to this email address
              </p>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full h-12 px-6 py-3 font-semibold rounded-xl shadow-lg transition-all duration-200 flex justify-center items-center ${
                  isLoading
                    ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white cursor-not-allowed shadow-none transform-none'
                    : 'bg-[#0E1696]  hover:from-primary-600 hover:to-primary-700 !text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner white mr-3 w-5 h-5"></div>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Create new account
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
